import React, { forwardRef, useMemo } from 'react';
import {
  Box,
  UtilityText,
  styled,
  type GDSCustomizableComponent,
  type GDSStatus,
} from '@leagueplatform/genesis-core';
import { calculateNormalizedPercentage } from './utils/calculate-normalized-percentage';
import { calculateTickPosition } from './utils/calculate-tick-position';

const DISPLAY_NAME = 'Thermobar';

export interface ThermobarProps extends GDSCustomizableComponent {
  /**
   * The human-readable text alternative of the value.
   */
  valueText?: string;
  /**
   * The current value to display on the thermobar.
   */
  value: number;
  /**
   * The minimum value of the range (start of the bar).
   * @default 0
   */
  min?: number;
  /**
   * The maximum value of the range (end of the bar).
   * @default 100
   */
  max?: number;
  /**
   * Tick marks to display along the bar.
   */
  tickmarks?: {
    value: number;
    label?: string;
  }[];
  /**
   * The style of the indicator (fill color).
   * @default 'brand'
   */
  indicatorStyle?: GDSStatus;
  /**
   * Accessible label for the thermobar.
   */
  accessibilityLabel: string;
}

const Container = styled(Box, {
  position: 'relative',
  width: '100%',
  variants: {
    hasLabels: {
      true: {
        // Reserve space for labels below the bar
        marginBottom: 'calc($lineHeightLabel + $quarter)',
      },
    },
  },
});

const Track = styled(Box, {
  position: 'relative',
  width: '100%',
  height: '$threeQuarters',
  backgroundColor: '$dataVizInterfaceTrackBackground',
  borderRadius: '$progressBar',
  borderWidth: '$borderWidths$thin',
  borderColor: '$dataVizInterfaceTrackBorder',
  borderStyle: 'solid',
  overflow: 'hidden',
});

const Indicator = styled(Box, {
  position: 'absolute',
  top: '50%',
  width: '$threeQuarters',
  height: '$threeQuarters',
  borderRadius: '50%',
  transform: 'translate(-50%, -50%)',
  variants: {
    status: {
      brand: { backgroundColor: '$dataVizSingleColorBrand' },
      neutral: { backgroundColor: '$dataVizSingleColorNeutral' },
      success: { backgroundColor: '$dataVizStatusSuccess' },
      warning: { backgroundColor: '$dataVizStatusWarning' },
      error: { backgroundColor: '$dataVizStatusCritical' },
      info: { backgroundColor: '$highlightIcon' },
    },
  },
  defaultVariants: {
    status: 'brand',
  },
});

const TickLine = styled(Box, {
  position: 'absolute',
  top: '50%',
  width: '$borderWidths$thin',
  height: '100%',
  backgroundColor: '$dataVizInterfaceTrackBorder',
  transform: 'translate(-50%, -50%)',
  zIndex: 1,
});

const LabelsContainer = styled(Box, {
  position: 'absolute',
  top: '100%',
  left: 0,
  width: '100%',
  marginTop: '$quarter',
  height: '$lineHeightLabel',
});

const TickLabel = styled(UtilityText, {
  position: 'absolute',
  whiteSpace: 'nowrap',
  variants: {
    alignment: {
      left: {
        transform: 'none',
        textAlign: 'left',
      },
      center: {
        transform: 'translateX(-50%)',
        textAlign: 'center',
      },
      right: {
        transform: 'translateX(-100%)',
        textAlign: 'right',
      },
    },
  },
  defaultVariants: {
    alignment: 'center',
  },
});

export const Thermobar = forwardRef<HTMLDivElement, ThermobarProps>(
  (
    {
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      valueText,
      value,
      min = 0,
      max = 100,
      tickmarks = [],
      indicatorStyle = 'brand',
      accessibilityLabel,
      className,
      css,
    },
    ref,
  ) => {
    // Calculate normalized percentage for indicator position
    const percentage = useMemo(
      () => calculateNormalizedPercentage(value, min, max),
      [value, min, max],
    );

    // Calculate positions for tickmarks
    const positionedTicks = useMemo(
      () =>
        tickmarks.map((tick) => {
          const position = calculateTickPosition(tick.value, min, max);

          return {
            ...tick,
            ...position,
          };
        }),
      [tickmarks, min, max],
    );

    const hasTickLabels = positionedTicks.some((tick) => tick.label);

    return (
      <Container
        ref={ref}
        hasLabels={hasTickLabels}
        css={css}
        className={['GDS-thermobar', className].join(' ')}
        data-testid={testId}
      >
        <Track
          role="meter"
          aria-valuetext={valueText}
          aria-valuenow={value}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-label={accessibilityLabel}
          className="GDS-thermobar-track"
        >
          <Indicator
            status={indicatorStyle}
            css={{ left: `${percentage}%` }}
            className="GDS-thermobar-indicator"
          />
          {positionedTicks.map((tick) => (
            <TickLine
              key={`tick-${tick.value}`}
              css={{ left: tick.left }}
              className="GDS-thermobar-tick"
            />
          ))}
        </Track>
        {hasTickLabels && (
          <LabelsContainer
            data-testid={`${testId}-labels`}
            aria-hidden="true"
            className="GDS-thermobar-labels"
          >
            {positionedTicks.map((tick) => (
              <TickLabel
                key={`label-${tick.value}`}
                css={{ left: tick.left }}
                alignment={tick.alignment}
                emphasis="subtle"
                size="xs"
                className="GDS-thermobar-tick-label"
              >
                {tick.label}
              </TickLabel>
            ))}
          </LabelsContainer>
        )}
      </Container>
    );
  },
);

Thermobar.displayName = DISPLAY_NAME;
